import React, { useEffect, useState } from "react";
import { Newspaper, ExternalLink, ArrowRight } from "lucide-react";
import { getLocalNews } from "@/lib/api";
import { T } from "@/context/LanguageContext";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export function LocalNews({ geoId, placeName }) {
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let ok = true;
    setItems(null);
    getLocalNews(geoId, 7).then((d) => { if (ok) { setItems(d.items || []); setQuery(d.query || ""); } }).catch(() => ok && setItems([]));
    return () => { ok = false; };
  }, [geoId]);

  if (items && items.length === 0) return null; // graceful: hide when nothing found

  const gnews = `https://news.google.com/search?q=${encodeURIComponent(query || placeName)}`;

  return (
    <section data-testid="local-news">
      <div className="mb-4 flex items-end justify-between gap-3 border-b border-border pb-3">
        <h2 className="flex items-center gap-2.5 font-display text-2xl font-black tracking-tight text-navy sm:text-3xl">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-oldglory-red text-white"><Newspaper className="h-5 w-5" /></span>
          <T>Local News</T>
        </h2>
        <a href={gnews} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-oldglory-red hover:text-oldglory-dark" data-testid="local-news-more">
          <T>More</T> <ArrowRight className="h-4 w-4" />
        </a>
      </div>

      {!items ? (
        <div className="grid gap-3 sm:grid-cols-2"><div className="h-20 animate-pulse rounded-xl bg-parchment" /><div className="h-20 animate-pulse rounded-xl bg-parchment" /></div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="local-news-list">
          {items.map((n, i) => (
            <a key={i} href={n.link} target="_blank" rel="noopener noreferrer"
              className="group flex flex-col rounded-xl border border-border bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-navy/30 hover:shadow-md"
              data-testid={`news-item-${i}`}>
              <div className="mb-1.5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider">
                <span className="rounded bg-navy/5 px-1.5 py-0.5 text-navy">{n.source || "News"}</span>
                <span className="text-muted-foreground">{timeAgo(n.published)}</span>
              </div>
              <h3 className="font-display text-base font-bold leading-snug text-navy group-hover:text-oldglory-red">{n.title}</h3>
              {n.snippet && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{n.snippet}</p>}
              <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-oldglory-red opacity-0 transition-opacity group-hover:opacity-100">
                <T>Read at source</T> <ExternalLink className="h-3 w-3" />
              </span>
            </a>
          ))}
        </div>
      )}
      <p className="mt-3 text-[10px] text-muted-foreground"><T>Headlines aggregated from Google News. Links open the original publisher.</T></p>
    </section>
  );
}
